import React, { useMemo } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { Link } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import { DataTable } from 'components/DataTable';
import { DataTableColumn, TableQueryParams, PaginatedResponse } from 'types/table.types';
import { SHOP_PATHS } from 'routes/routesConstants';
import { useShop } from 'context/shopContext';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { DataTableApiService } from 'services/api/tableApi';
import { formatRelativeDate } from 'utils/dateFormatter';
import { fmt } from 'utils/common';

// ─── Type ─────────────────────────────────────────────────────────────────────

interface PurchaseAdjustmentItem {
  id: string;
  purchaseInvoiceAdjustmentId: string;
  shopItemId: string;
  shopItemName?: string;
  invoiceId: string;
  adjustmentReason: string;
  adjustmentStatus: string;
  originalQuantity: number;
  originalUnitPrice: number;
  quantityDelta: number;
  priceDelta: number;
  discountDelta: number;
  adjustmentTypes: string[];
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Purchase-side financial effect (money in = positive)
 * Price decrease (6→3) = we receive money → positive
 */
function calcNetDelta(item: PurchaseAdjustmentItem): number {
  const newPrice = item.originalUnitPrice + item.priceDelta;
  const priceEffect = item.originalQuantity * item.priceDelta;
  const qtyEffect = item.quantityDelta * newPrice;
  return -(priceEffect + qtyEffect - item.discountDelta);
}

const TYPE_META: Record<string, { label: string; color: string; bg: string }> = {
  QuantityChange: { label: 'Qty change', color: '#2563eb', bg: '#eff6ff' },
  PriceChange: { label: 'Price change', color: '#059669', bg: '#ecfdf5' },
  ItemRemoval: { label: 'Item removal', color: '#dc2626', bg: '#fef2f2' },
  DiscountChange: { label: 'Discount change', color: '#d97706', bg: '#fffbeb' },
};

// ─── Mini Components ──────────────────────────────────────────────────────────

const TypePill: React.FC<{ type: string }> = ({ type }) => {
  const m = TYPE_META[type] ?? { label: type, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        background: m.bg,
        color: m.color,
      }}
    >
      {m.label}
    </span>
  );
};

const DeltaChip: React.FC<{ value: number; suffix?: string }> = ({ value, suffix = '' }) => {
  if (value === 0) return <span style={{ color: '#9ca3af' }}>—</span>;
  const positive = value > 0;
  return (
    <span
      style={{ fontFamily: 'monospace', fontWeight: 700, color: positive ? '#059669' : '#dc2626' }}
    >
      {positive ? '+' : ''}
      {value}
      {suffix}
    </span>
  );
};

const MoneyChip: React.FC<{ value: number }> = ({ value }) => {
  if (value === 0) return <span style={{ color: '#9ca3af' }}>—</span>;
  const positive = value > 0;
  return (
    <span
      style={{ fontFamily: 'monospace', fontWeight: 700, color: positive ? '#059669' : '#dc2626' }}
    >
      {positive ? '+' : ''}
      {fmt(value)}
    </span>
  );
};

const OriginalSnapshot: React.FC<{
  qty: number;
  price: number;
  qtyDelta: number;
  priceDelta: number;
}> = ({ qty, price, qtyDelta, priceDelta }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <div>
      Qty: <span style={{ textDecoration: qtyDelta ? 'line-through' : 'none' }}>{qty}</span>
      {qtyDelta !== 0 && <> → {qty + qtyDelta}</>}
    </div>
    <div>
      Price:{' '}
      <span style={{ textDecoration: priceDelta ? 'line-through' : 'none' }}>{fmt(price)}</span>
      {priceDelta !== 0 && <> → {fmt(price + priceDelta)}</>}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const PurchaseAdjustmentItemsList: React.FC = () => {
  const { shop } = useShop();
  const shopId = shop?.id ?? '';

  const columns = useMemo<DataTableColumn<PurchaseAdjustmentItem>[]>(
    () => [
      {
        accessorKey: 'shopItemName',
        id: 'shopItemName',
        header: 'Item',
        enableSorting: false,
        enableFiltering: true,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Link
                to={SHOP_PATHS.PURCHASE_INVOICE_DETAIL.replace(':shopId', shopId).replace(
                  ':invoiceId',
                  row.invoiceId,
                )}
                style={{ fontWeight: 600 }}
              >
                {row.shopItemName || '—'}
              </Link>

              <div style={{ display: 'flex', gap: 4 }}>
                {row.adjustmentTypes?.map((t) => (
                  <TypePill key={t} type={t} />
                ))}
              </div>
            </div>
          );
        },
      },

      {
        id: 'changes',
        header: 'What changed',
        cell: (info) => {
          const row = info.row.original;
          return (
            <OriginalSnapshot
              qty={row.originalQuantity}
              price={row.originalUnitPrice}
              qtyDelta={row.quantityDelta}
              priceDelta={row.priceDelta}
            />
          );
        },
      },

      {
        id: 'netEffect',
        header: 'Cost effect',
        cell: (info) => {
          const row = info.row.original;
          const net = calcNetDelta(row);
          return (
            <div>
              <MoneyChip value={net} />
              {row.discountDelta !== 0 && (
                <div style={{ fontSize: 11 }}>
                  Disc: <DeltaChip value={row.discountDelta} suffix="%" />
                </div>
              )}
            </div>
          );
        },
      },

      {
        accessorKey: 'adjustmentReason',
        header: 'Reason',
        enableFiltering: true,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div style={{ maxWidth: 220 }}>
              <div>{row.adjustmentReason}</div>
              {row.notes && <div style={{ fontSize: 11, color: '#9ca3af' }}>{row.notes}</div>}
            </div>
          );
        },
      },

      {
        id: 'invoice',
        header: 'Invoice',
        cell: (info) => {
          const row = info.row.original;
          return (
            <Link
              to={SHOP_PATHS.PURCHASE_INVOICE_DETAIL.replace(':shopId', shopId).replace(
                ':invoiceId',
                row.invoiceId,
              )}
            >
              View →
            </Link>
          );
        },
      },

      {
        accessorKey: 'adjustmentStatus',
        header: 'Status',
        enableSorting: true,
        enableFiltering: true,
        cell: (info) => {
          const status = info.getValue() as string;
          const isPosted = status === 'Posted';
          return (
            <span style={{ color: isPosted ? '#059669' : '#d97706', fontWeight: 600 }}>
              {status}
            </span>
          );
        },
      },

      {
        accessorKey: 'createdAt',
        header: 'Date',
        enableSorting: true,
        cell: (info) => {
          const { text, isStale } = formatRelativeDate(info.getValue() as string);
          return <span style={{ color: isStale ? '#dc2626' : '#6b7280' }}>{text}</span>;
        },
      },
    ],
    [shopId],
  );

  const handleFetchData = async (
    params: TableQueryParams,
  ): Promise<PaginatedResponse<PurchaseAdjustmentItem>> => {
    return DataTableApiService.fetchPaginatedData<PurchaseAdjustmentItem>(
      API_ENDPOINTS.TENANT.PURCHASES.ADJUSTMENT_ITEMS_PAGINATED.replace(':shopId', shopId),
      { ...params, filters: { ...params.filters, shopId } },
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">
            <div style={{ marginBottom: 16 }}>
              <h5>Purchase adjustments</h5>
              <p style={{ fontSize: 12, color: '#6b7280' }}>
                Original purchase invoices are never modified — every row is a correction record.
              </p>
            </div>

            <DataTable
              columns={columns}
              queryKey={API_ENDPOINTS.TENANT.PURCHASES.ADJUSTMENT_ITEMS_PAGINATED.replace(
                ':shopId',
                shopId,
              )}
              onFetchData={handleFetchData}
              enableSearch
              enableFiltering
              enablePagination
              enableSorting
              enableColumnVisibility
              enableExport
              defaultPageSize={10}
              emptyMessage="No purchase adjustment items found."
              striped
              hover
              bordered
              responsive
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PurchaseAdjustmentItemsList;
